import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { BRAND, NAV } from "@/lib/brand";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 w-full transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur border-b border-[#E2E8F0] shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-20 items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="navbar-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={BRAND.logo}
              alt="RK Enterprises"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
            />
            <div className="leading-tight">
              <div className="font-display text-lg sm:text-xl font-extrabold text-[#0F172A] tracking-tight">
                RK Enterprises
              </div>
              <div className="text-[10px] sm:text-xs text-[#1F6B5C] font-semibold tracking-widest uppercase">
                Manpower Services
              </div>
            </div>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-1"
            data-testid="navbar-desktop-nav"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                    isActive
                      ? "text-[#1E3A8A] bg-[#1E3A8A]/5"
                      : "text-[#0F172A] hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`}
              data-testid="navbar-phone-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-[#1E3A8A]"
            >
              <Phone className="h-4 w-4 text-[#1F6B5C]" />
              {BRAND.phones[0]}
            </a>
            <Button
              asChild
              data-testid="navbar-hire-cta"
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm"
            >
              <Link to="/contact?intent=hire">Hire Workforce</Link>
            </Button>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="navbar-mobile-toggle"
                aria-label="Toggle menu"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[300px] sm:w-[360px] p-0"
              data-testid="navbar-mobile-sheet"
            >
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">
                Site navigation links for RK Enterprises
              </SheetDescription>
              <div className="flex items-center gap-3 p-5 border-b">
                <img
                  src={BRAND.logo}
                  alt="RK Enterprises"
                  className="h-10 w-10 object-contain"
                />
                <div className="font-display font-extrabold text-[#0F172A]">
                  RK Enterprises
                </div>
              </div>
              <nav className="flex flex-col p-2">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `px-4 py-3 text-base font-medium rounded-sm ${
                        isActive
                          ? "text-[#1E3A8A] bg-[#1E3A8A]/5"
                          : "text-[#0F172A] hover:bg-[#1E3A8A]/5"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-4 border-t mt-auto space-y-3">
                <a
                  href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`}
                  className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"
                  data-testid="mobile-navbar-phone-link"
                >
                  <Phone className="h-4 w-4 text-[#1F6B5C]" />
                  {BRAND.phones[0]}
                </a>
                <Button
                  asChild
                  className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-sm"
                  data-testid="mobile-navbar-hire-cta"
                >
                  <Link to="/contact?intent=hire">Hire Workforce</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
